import { useState, useEffect, useCallback } from "react";
import api from "../api";

const CACHE_KEY = "exam_mode_cache_v2";
const CACHE_TTL = 5 * 60 * 1000; // 5 minutes

export default function useExamMode(studentId = null) {
  const [mockExamsMode, setMockExamsMode] = useState("actual");
  const [worksheetsMode, setWorksheetsMode] = useState("actual");
  const [toggleEnabled, setToggleEnabled] = useState(false);
  const [preferenceMode, setPreferenceMode] = useState("actual");
  const [practiceForced, setPracticeForced] = useState(false);
  const [loading, setLoading] = useState(true);
  const [settingMode, setSettingMode] = useState(false);

  const role = (localStorage.getItem("role") || "").toLowerCase();
  const isParent = role === "parent";
  const basePath = isParent ? "/parent" : "/student";

  const applyModeData = useCallback((data = {}) => {
    const mockMode = data.mock_exams || "actual";
    const wsMode = data.worksheets || "actual";
    const toggle = data.toggle_enabled !== false;
    const preference = data.preference_mode || mockMode;
    const forced = !!data.practice_forced;

    setMockExamsMode(mockMode);
    setWorksheetsMode(wsMode);
    setToggleEnabled(toggle);
    setPreferenceMode(preference);
    setPracticeForced(forced);

    return { mockMode, wsMode, toggle, preference, forced };
  }, []);

  const fetchMode = useCallback(async () => {
    let hasFreshCache = false;
    try {
      // Cache key scoped to student when parent
      const cacheKey = isParent && studentId ? `${CACHE_KEY}_${studentId}` : CACHE_KEY;

      // Check sessionStorage cache first
      const cached = sessionStorage.getItem(cacheKey);
      if (cached) {
        const parsed = JSON.parse(cached);
        if (Date.now() - parsed.timestamp < CACHE_TTL) {
          applyModeData(parsed);
          setLoading(false);
          hasFreshCache = true;
        }
      }

      const params = {};
      if (isParent && studentId) params.student_id = studentId;

      const res = await api.get(`${basePath}/exam-mode`, { params });
      const data = res.data?.data || {};
      const applied = applyModeData(data);

      sessionStorage.setItem(
        cacheKey,
        JSON.stringify({
          mock_exams: applied.mockMode,
          worksheets: applied.wsMode,
          toggle_enabled: applied.toggle,
          preference_mode: applied.preference,
          practice_forced: applied.forced,
          timestamp: Date.now(),
        })
      );
    } catch {
      // Keep fresh cached values on request failure; fallback to actual only when nothing cached.
      if (!hasFreshCache) {
        setMockExamsMode("actual");
        setWorksheetsMode("actual");
        setToggleEnabled(false);
        setPreferenceMode("actual");
        setPracticeForced(false);
      }
    } finally {
      // If cache already rendered, this is a background refresh.
      if (!hasFreshCache) setLoading(false);
    }
  }, [applyModeData, basePath, isParent, studentId]);

  useEffect(() => {
    fetchMode();
  }, [fetchMode]);

  const isTestingMode = useCallback(
    (scope) => {
      if (scope === "mock_exams") return mockExamsMode === "testing";
      // Past papers use the same portal practice toggle as mock exams (no purchase bypass).
      if (scope === "past_papers") return mockExamsMode === "testing";
      if (scope === "worksheets") return worksheetsMode === "testing";
      return mockExamsMode === "testing" || worksheetsMode === "testing";
    },
    [mockExamsMode, worksheetsMode]
  );

  const refresh = useCallback(() => {
    const cacheKey = isParent && studentId ? `${CACHE_KEY}_${studentId}` : CACHE_KEY;
    sessionStorage.removeItem(cacheKey);
    setLoading(true);
    fetchMode();
  }, [fetchMode, isParent, studentId]);

  const setMode = useCallback(
    async (mode, targetStudentId = null) => {
      setSettingMode(true);
      try {
        const payload = { mode };
        if (isParent && (targetStudentId || studentId)) {
          payload.student_id = targetStudentId || studentId;
        }
        const res = await api.post(`${basePath}/exam-mode`, payload);
        const data = res.data?.data || {};
        const applied = applyModeData({
          mock_exams: data.mock_exams || mode,
          worksheets: data.worksheets || mode,
          toggle_enabled: data.toggle_enabled !== false,
          preference_mode: data.preference_mode || mode,
          practice_forced: data.practice_forced,
        });
        const cacheKey = isParent && (targetStudentId || studentId)
          ? `${CACHE_KEY}_${targetStudentId || studentId}`
          : CACHE_KEY;
        sessionStorage.setItem(
          cacheKey,
          JSON.stringify({
            mock_exams: applied.mockMode,
            worksheets: applied.wsMode,
            toggle_enabled: applied.toggle,
            preference_mode: applied.preference,
            practice_forced: applied.forced,
            timestamp: Date.now(),
          })
        );
        window.dispatchEvent(
          new CustomEvent("exam-mode-changed", {
            detail: {
              mode: applied.mockMode,
              student_id: payload.student_id || null,
              practice_forced: applied.forced,
              at: Date.now(),
            },
          })
        );
        return data;
      } finally {
        setSettingMode(false);
      }
    },
    [applyModeData, basePath, isParent, studentId]
  );

  return {
    mockExamsMode,
    worksheetsMode,
    toggleEnabled,
    preferenceMode,
    practiceForced,
    isTestingMode,
    loading,
    settingMode,
    refresh,
    setMode,
  };
}
